import React from 'react';
import {
  GitBranch,
  Star,
  GitFork,
  ExternalLink,
  Code,
  Play,
  Package,
  Globe,
  Lock,
  Copy,
} from 'lucide-react';
import { LoadingSpinner } from '../common';
import type { Repository } from '../../types';

interface RepositoryHeaderProps {
  repository: Repository;
  isUpload: boolean;
  startingAnalysis: boolean;
  cloneUrlCopied: boolean;
  onStartAnalysis: () => void;
  onCopyCloneUrl: (url: string) => void;
  refType?: 'branch' | 'tag' | 'commitSha' | '';
  refValue?: string;
  onRefTypeChange?: (v: 'branch' | 'tag' | 'commitSha' | '') => void;
  onRefValueChange?: (v: string) => void;
}

export const RepositoryHeader: React.FC<RepositoryHeaderProps> = ({
  repository,
  isUpload,
  startingAnalysis,
  cloneUrlCopied,
  onStartAnalysis,
  onCopyCloneUrl,
  refType = '',
  refValue = '',
  onRefTypeChange,
  onRefValueChange,
}) => {
  const platformLabel = repository.platform || repository.source || 'unknown';
  const cloneUrl = repository.cloneUrl || repository.sshUrl;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xs border border-slate-200 dark:border-slate-700/50 mb-6">
      {/* Top section: name, description, actions */}
      <div className="p-6 relative z-10">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3">
              <h1
                className="text-2xl font-bold truncate"
                style={{ color: 'var(--color-text-primary)' }}
              >
                {repository.fullName || repository.name}
              </h1>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                {repository.private ? (
                  <>
                    <Lock className="w-3 h-3" /> Private
                  </>
                ) : (
                  <>
                    <Globe className="w-3 h-3" /> Public
                  </>
                )}
              </span>
            </div>
            {repository.description && (
              <p className="mt-1.5 line-clamp-2" style={{ color: 'var(--color-text-secondary)' }}>
                {repository.description}
              </p>
            )}

            {/* Primary meta: language, stars, forks, branch, platform */}
            <div
              className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-4 text-sm"
              style={{ color: 'var(--color-text-tertiary)' }}
            >
              {repository.language && (
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-blue-500" />
                  {repository.language}
                </span>
              )}
              {(repository.metadata?.stars || repository.stars) && (
                <span className="flex items-center gap-1.5">
                  <Star className="w-4 h-4" />
                  {(repository.metadata?.stars || repository.stars || 0).toLocaleString()}
                </span>
              )}
              {(repository.metadata?.forks || repository.forks) && (
                <span className="flex items-center gap-1.5">
                  <GitFork className="w-4 h-4" />
                  {(repository.metadata?.forks || repository.forks || 0).toLocaleString()}
                </span>
              )}
              {repository.defaultBranch && (
                <span className="flex items-center gap-1.5">
                  <GitBranch className="w-4 h-4" />
                  {repository.defaultBranch}
                </span>
              )}
              <span className="flex items-center gap-1.5">
                <Code className="w-4 h-4" />
                {platformLabel.charAt(0).toUpperCase() + platformLabel.slice(1)}
              </span>
              {repository.metadata?.size && (
                <span className="flex items-center gap-1.5">
                  <Package className="w-4 h-4" />
                  {(repository.metadata.size / 1024).toFixed(1)} MB
                </span>
              )}
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-3 shrink-0">
            {repository.url && (
              <a
                href={repository.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg"
              >
                <ExternalLink className="w-4 h-4" />
                View Source
              </a>
            )}
            {!isUpload && onRefTypeChange && (
              <div className="flex items-center gap-2">
                <select
                  value={refType}
                  onChange={(e) =>
                    onRefTypeChange(e.target.value as 'branch' | 'tag' | 'commitSha' | '')
                  }
                  disabled={startingAnalysis}
                  className="w-auto px-3 py-2.5 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200"
                >
                  <option value="">Default branch</option>
                  <option value="branch">Branch</option>
                  <option value="tag">Tag</option>
                  <option value="commitSha">Commit</option>
                </select>
                {refType && (
                  <input
                    type="text"
                    value={refValue}
                    onChange={(e) => onRefValueChange?.(e.target.value)}
                    placeholder={
                      refType === 'commitSha'
                        ? 'e.g. a1b2c3d4...'
                        : refType === 'tag'
                          ? 'e.g. v1.0.0'
                          : 'e.g. develop'
                    }
                    disabled={startingAnalysis}
                    className="w-36 px-3 py-2.5 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200"
                  />
                )}
              </div>
            )}
            <button
              onClick={onStartAnalysis}
              disabled={startingAnalysis}
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 disabled:cursor-not-allowed rounded-lg"
            >
              {startingAnalysis ? (
                <>
                  <LoadingSpinner size="sm" />
                  Starting...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" />
                  {isUpload ? 'Re-upload & Analyze' : 'New Analysis'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Details section: timestamps, clone URL, topics */}
      <div className="border-t border-slate-200 dark:border-slate-700/50 px-6 py-4 relative z-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
          {repository.createdAt && (
            <div>
              <dt
                className="text-xs font-medium uppercase tracking-wider mb-1"
                style={{ color: 'var(--color-text-tertiary)' }}
              >
                Created
              </dt>
              <dd style={{ color: 'var(--color-text-secondary)' }}>
                {new Date(repository.createdAt).toLocaleDateString(undefined, {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })}
              </dd>
            </div>
          )}

          {(repository.metadata?.updatedAt || repository.updatedAt) && (
            <div>
              <dt
                className="text-xs font-medium uppercase tracking-wider mb-1"
                style={{ color: 'var(--color-text-tertiary)' }}
              >
                Last Activity
              </dt>
              <dd style={{ color: 'var(--color-text-secondary)' }}>
                {new Date(
                  repository.metadata?.updatedAt || repository.updatedAt!,
                ).toLocaleDateString(undefined, {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })}
              </dd>
            </div>
          )}

          {repository.lastSyncedAt && (
            <div>
              <dt
                className="text-xs font-medium uppercase tracking-wider mb-1"
                style={{ color: 'var(--color-text-tertiary)' }}
              >
                Last Synced
              </dt>
              <dd style={{ color: 'var(--color-text-secondary)' }}>
                {new Date(repository.lastSyncedAt).toLocaleDateString(undefined, {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })}
              </dd>
            </div>
          )}

          {cloneUrl && (
            <div className="sm:col-span-2 lg:col-span-4">
              <dt
                className="text-xs font-medium uppercase tracking-wider mb-1"
                style={{ color: 'var(--color-text-tertiary)' }}
              >
                Clone URL
              </dt>
              <dd className="flex items-center gap-2">
                <span
                  className="font-mono text-xs truncate"
                  style={{ color: 'var(--color-text-secondary)' }}
                  title={cloneUrl}
                >
                  {cloneUrl}
                </span>
                <button
                  onClick={() => onCopyCloneUrl(cloneUrl)}
                  className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 relative shrink-0"
                  title="Copy clone URL"
                >
                  <Copy className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
                  {cloneUrlCopied && (
                    <span className="absolute -top-7 left-1/2 -translate-x-1/2 px-2 py-0.5 text-xs font-medium text-white bg-gray-900 dark:bg-gray-100 dark:text-gray-900 rounded shadow-xs whitespace-nowrap">
                      Copied!
                    </span>
                  )}
                </button>
              </dd>
            </div>
          )}
        </div>

        {/* Topics */}
        {(() => {
          const meta = repository.metadata as Record<string, unknown> | undefined;
          const topics = Array.isArray(meta?.topics) ? (meta.topics as string[]) : null;
          if (!topics || topics.length === 0) return null;
          return (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {topics.map((topic: string) => (
                <span
                  key={topic}
                  className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300"
                >
                  {topic}
                </span>
              ))}
            </div>
          );
        })()}
      </div>
    </div>
  );
};
